import { t } from "@/i18n-js/instance";
import { isTrialThenValue } from "@/react/helpers/paywallPriceHelpers";
import type { PaywallCheckoutPreview } from "@circle-react/types";
import type { PreviewComponents } from "../../../types";
import { shouldExpandFirstPayment } from "../../FirstPaymentPreview/variants/helpers";
import { Lines } from "../Lines";
import { shouldExpandFuturePayments } from "./helpers";

const getTooltipText = (
  couponDuration: string,
  count: string | number | undefined,
) =>
  t(["paywall_checkout.future_payments.discount_phrase_v2", couponDuration], {
    count,
  });

export const subscriptionVariant: PreviewComponents = [
  [
    Lines.HeaderLine,
    (preview: PaywallCheckoutPreview) => {
      if (isTrialThenValue(preview.preview_scenario)) {
        return {
          label: t(
            "paywall_checkout.future_payments.future_payments_after_x_trial_days",
            { count: preview?.trial_period?.days },
          ),
          value: shouldExpandFirstPayment(preview)
            ? preview.first_payment.formatted_amount_due
            : null,
        };
      }

      return {
        value: shouldExpandFuturePayments(preview)
          ? preview?.future_payments?.formatted_amount_due
          : null,
      };
    },
  ],
  [
    Lines.RegularPriceLine,
    (preview: PaywallCheckoutPreview) => {
      if (isTrialThenValue(preview.preview_scenario)) {
        return {
          value: preview.first_payment.formatted_subtotal,
        };
      }

      return {
        value: preview.future_payments?.formatted_subtotal,
      };
    },
  ],
  [
    Lines.DiscountLine,
    (preview: PaywallCheckoutPreview) => {
      if (!preview.paywall_coupon) {
        return {};
      }

      const couponDuration = preview?.paywall_coupon?.duration;
      const couponApplicationCount =
        (preview?.future_payments?.discount_phrase?.match(/(\d+)/) || [])[0];

      if (isTrialThenValue(preview.preview_scenario)) {
        return {
          value: preview.first_payment.formatted_discount,
          tooltip: getTooltipText(couponDuration, couponApplicationCount),
        };
      }

      return {
        tooltip: getTooltipText(couponDuration, couponApplicationCount),
      };
    },
  ],
  [
    Lines.TaxLine,
    (preview: PaywallCheckoutPreview) => {
      if (isTrialThenValue(preview.preview_scenario)) {
        return {
          value: preview.first_payment.formatted_tax,
        };
      }

      return {
        value: preview?.future_payments?.formatted_tax,
      };
    },
  ],
  [
    Lines.SubtotalLine,
    (preview: PaywallCheckoutPreview) => {
      if (isTrialThenValue(preview.preview_scenario)) {
        const paywallPrice = preview.paywall_price;
        const dateString = preview.first_payment.formatted_due_on;

        return {
          label: t(
            [
              "paywall_checkout.future_payments.payments_starting_on_phrase",
              paywallPrice.price_type,
              paywallPrice.interval_type,
            ],
            {
              date: dateString,
            },
          ),
          value: preview?.first_payment?.formatted_amount_due,
          valueWeight: shouldExpandFirstPayment(preview)
            ? "normal"
            : "semibold",
        };
      }

      return {
        value: preview?.future_payments?.formatted_amount_due,
        valueWeight: shouldExpandFuturePayments(preview)
          ? "normal"
          : "semibold",
      };
    },
  ],
];
